import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BackToTopButton from "@/components/common/BackToTopButton";

// Background photo component
const BackgroundPhoto = ({
  top,
  left,
  width,
  height,
  delay,
  src,
}: {
  top: number;
  left: number;
  width: string;
  height: string;
  delay: number;
  src: string;
}) => (
  <div
    className="absolute rounded-xl overflow-hidden"
    style={{
      top: `${top}%`,
      left: `${left}%`,
      width: width,
      height: height,
      animation: `float 20s ease-in-out ${delay}s infinite`,
      zIndex: 1,
    }}
  >
    <img
      src={src}
      alt=""
      loading="eager"
      className="w-full h-full object-cover opacity-70 blur-xs"
      style={{ transform: "scale(1.1)" }}
    />
  </div>
);

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  const [displayPhotos, setDisplayPhotos] = useState<Array<any>>([]);
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  // Background photos
  const photos = [
    {
      width: "450px",
      height: "350px",
      top: 15,
      left: 10,
      delay: 0,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG2.jpg",
    },
    {
      width: "400px",
      height: "300px",
      top: 15,
      left: 60,
      delay: 1,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG3.jpg",
    },
    {
      width: "420px",
      height: "320px",
      top: 60,
      left: 10,
      delay: 2,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG1.jpg",
    },
    {
      width: "430px",
      height: "330px",
      top: 60,
      left: 60,
      delay: 2.5,
      src: "https://public.waytora.com/Waytora%20About%20IMGs/IMG5.jpg",
    },
  ];

  // Listen for window resize and update state
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    updatePhotos();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update photos when window width changes
  useEffect(() => {
    updatePhotos();
  }, [windowWidth]);

  // Responsive photo config
  const updatePhotos = () => {
    if (windowWidth < 640) {
      setDisplayPhotos(photos.slice(0, 1));
    } else if (windowWidth < 1024) {
      setDisplayPhotos(photos.slice(0, 2));
    } else {
      setDisplayPhotos(photos);
    }
  };

  return (
    <div className="relative w-full no-scrollbar min-h-screen flex flex-col bg-black text-white overflow-hidden">
      {/* Animation style */}
      <style>
        {`
        @keyframes float {
          0% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(-10px, 10px) rotate(1deg); }
          50% { transform: translate(0, 20px) rotate(0deg); }
          75% { transform: translate(10px, 10px) rotate(-1deg); }
          100% { transform: translate(0, 0) rotate(0deg); }
        }
        `}
      </style>

      {/* Background Photos Layer */}
      <div className="absolute inset-0 overflow-hidden">
        {displayPhotos.map((photo, index) => (
          <BackgroundPhoto
            key={index}
            width={windowWidth < 640 ? "100%" : photo.width}
            height={windowWidth < 640 ? "100%" : photo.height}
            top={windowWidth < 640 ? 0 : photo.top}
            left={windowWidth < 640 ? 0 : photo.left}
            delay={photo.delay}
            src={photo.src}
          />
        ))}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-[2]"></div>
      </div>

      {/* Header */}
      <Header showHomeButton={true} />

      {/* Main Content */}
      <main className="flex-grow flex items-start justify-center px-4 py-6 sm:py-8 overflow-y-auto">
        <div className="w-full max-w-3xl p-6 sm:p-8 rounded-xl border border-white/20 bg-black/40 backdrop-blur-md shadow-2xl z-10 my-8">
          <h1 className="text-2xl sm:text-3xl font-light mb-6 text-white/90">
            Privacy Policy
          </h1>
          <div className="space-y-6 text-sm sm:text-base text-gray-300 leading-relaxed">
            <p className="mb-2">
              <span className="font-semibold">Waytora Pty Ltd</span>
              <br />
              <span>
                Last updated: <span className="font-semibold">1 May 2025</span>
              </span>
            </p>
            <hr className="border-white/20 my-4" />
            <h2 className="text-xl text-white/90 font-medium mt-6">
              1. Introduction
            </h2>
            <p>
              Waytora Pty Ltd (ABN TBD) ("Waytora", "we", "our", "us") respects
              your privacy. This Privacy Policy (the "Policy") explains how we
              collect, use, disclose, store and protect Personal Information*
              when you visit <span className="font-semibold">waytora.com</span>{" "}
              or use any related websites, mobile or desktop applications, APIs,
              widgets, email communications, or other online properties that
              link to or reference this Policy (collectively, the "Services").
            </p>
            <p>
              The Services help users research, design, organise, share and book
              travel itineraries and associated products. By accessing or using
              the Services, you confirm that you have read and understood this
              Policy and our{" "}
              <span
                onClick={() => navigate("/terms-of-service")}
                className="underline cursor-pointer text-indigo-400 hover:text-indigo-300"
              >
                [Terms of Service]
              </span>
              . If you do not agree, please do not use the Services.
            </p>
            <p className="italic text-xs text-gray-400">
              *"Personal Information" means information relating to an
              identified or reasonably identifiable natural person and includes
              "personal data" under the EU/UK GDPR, "personal information" under
              the California Consumer Privacy Act (CCPA/CPRA) and "personal
              information" under the Australian Privacy Act 1988 (Cth).
            </p>
            <hr className="border-white/20 my-4" />
            <h2 className="text-xl text-white/90 font-medium mt-6">
              2. Who We Are &amp; Contact Details
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-gray-300 border border-white/10 rounded-lg mb-4">
                <tbody>
                  <tr className="border-b border-white/10">
                    <th className="pr-4 py-1 font-medium">Legal entity</th>
                    <td className="py-1">
                      Waytora Pty Ltd, incorporated in Australia
                    </td>
                  </tr>
                  <tr className="border-b border-white/10">
                    <th className="pr-4 py-1 font-medium">
                      Registered &amp; trading address
                    </th>
                    <td className="py-1">
                      Level 1, 457-459 Elizabeth Street, Surry Hills NSW 2010,
                      Australia
                    </td>
                  </tr>
                  <tr className="border-b border-white/10">
                    <th className="pr-4 py-1 font-medium">
                      EU/UK Article 27 representative
                    </th>
                    <td className="py-1">support@waytora.com</td>
                  </tr>
                  <tr className="border-b border-white/10">
                    <th className="pr-4 py-1 font-medium">Privacy contact</th>
                    <td className="py-1">support@waytora.com</td>
                  </tr>
                  <tr>
                    <th className="pr-4 py-1 font-medium">
                      Data Protection Officer (DPO)
                    </th>
                    <td className="py-1">
                      Not required under current thresholds; queries handled by
                      Privacy Team
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <hr className="border-white/20 my-4" />
            <h2 className="text-xl text-white/90 font-medium mt-6">3. Scope</h2>
            <p>
              This Policy applies to Personal Information that we control or
              process in connection with the Services. It{" "}
              <span className="font-semibold">does not</span> apply to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                Websites, apps or services operated by third parties (even if we
                link to them or facilitate bookings).
              </li>
              <li>
                Information processed by travel suppliers, payment processors,
                advertising networks or social-media sites under their own
                privacy statements.
              </li>
            </ul>
            <hr className="border-white/20 my-4" />
            <h2 className="text-xl text-white/90 font-medium mt-6">
              4. Information We Collect
            </h2>
            <p>
              We collect the categories of information listed below. Specific
              data elements collected may vary depending on your interactions
              with the Services.
            </p>
            <h3 className="font-semibold mt-4">
              4.1 Data You Provide Directly
            </h3>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                <span className="font-semibold">Account details —</span> name,
                email address, Google OAuth identifier, password hash (if
                applicable).
              </li>
              <li>
                <span className="font-semibold">
                  Profile &amp; preferences —
                </span>{" "}
                travel interests, favourite destinations, loyalty-programme
                numbers you optionally store.
              </li>
              <li>
                <span className="font-semibold">User-generated content —</span>{" "}
                photos, trip names, notes, ratings, reviews, itinerary details,
                messages sent to our AI assistant "Tora".
              </li>
              <li>
                <span className="font-semibold">Communications —</span>{" "}
                enquiries, survey responses, feedback, support tickets,
                marketing preferences.
              </li>
              <li>
                <span className="font-semibold">Payment data —</span> limited
                billing identifiers (e.g. last 4 digits of card, postcode)
                handled by our payment processors. We do{" "}
                <span className="font-semibold">not</span> store full card
                numbers.
              </li>
            </ul>
            <h3 className="font-semibold mt-4">
              4.2 Data We Collect Automatically
            </h3>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                <span className="font-semibold">
                  Device &amp; technical data —
                </span>{" "}
                IP address, browser type, OS, device model, language, screen
                resolution, referring URL, crash logs.
              </li>
              <li>
                <span className="font-semibold">Usage data —</span> pages
                viewed, features used, click paths, session duration, in-app
                events, date/time stamps.
              </li>
              <li>
                <span className="font-semibold">Approximate location —</span>{" "}
                city/region derived from IP address; precise coordinates only if
                you actively share them (e.g. to find nearby attractions).
              </li>
              <li>
                <span className="font-semibold">Cookies &amp; similar —</span>{" "}
                session cookies, security cookies, analytics tokens,
                error-tracking beacons.
              </li>
            </ul>
            <h3 className="font-semibold mt-4">4.3 Data from Third Parties</h3>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                <span className="font-semibold">Social-login providers —</span>{" "}
                if you sign-in with Google, we receive your verified name and
                email.
              </li>
              <li>
                <span className="font-semibold">
                  Affiliate/booking partners —
                </span>{" "}
                confirmation numbers and booking metadata to display travel
                reservations in your account.
              </li>
              <li>
                <span className="font-semibold">
                  Analytics &amp; diagnostics vendors —
                </span>{" "}
                aggregated crash or performance metrics tied to device IDs.
              </li>
            </ul>
            <p>
              We do <span className="font-semibold">not</span> intentionally
              collect: sensitive health data, government identifiers, biometric
              templates, credit reports, or data from children under 13.
            </p>
            <hr className="border-white/20 my-4" />
            <h2 className="text-xl text-white/90 font-medium mt-6">
              5. Purposes &amp; Legal Bases for Processing
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-gray-300 border border-white/10 rounded-lg mb-4">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="pr-4 py-1 font-medium">Purpose</th>
                    <th className="pr-4 py-1 font-medium">Typical data</th>
                    <th className="py-1 font-medium">GDPR legal basis</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-white/10">
                    <td>Create &amp; manage accounts</td>
                    <td>Account details</td>
                    <td>Contract (Art 6 (1)(b))</td>
                  </tr>
                  <tr className="border-b border-white/10">
                    <td>Provide itinerary tools &amp; AI assistant</td>
                    <td>Account, content</td>
                    <td>Contract / Legitimate interest</td>
                  </tr>
                  <tr className="border-b border-white/10">
                    <td>Facilitate reservations with partners</td>
                    <td>Trip details</td>
                    <td>Contract</td>
                  </tr>
                  <tr className="border-b border-white/10">
                    <td>Personalise content &amp; recommendations</td>
                    <td>Preferences, usage</td>
                    <td>Legitimate interest / Consent (where required)</td>
                  </tr>
                  <tr className="border-b border-white/10">
                    <td>Analytics, debugging, fraud prevention</td>
                    <td>Device, usage</td>
                    <td>Legitimate interest</td>
                  </tr>
                  <tr className="border-b border-white/10">
                    <td>Marketing emails (opt-out)</td>
                    <td>Account details</td>
                    <td>Legitimate interest</td>
                  </tr>
                  <tr>
                    <td>Compliance, legal claims, audits</td>
                    <td>Any relevant</td>
                    <td>Legal obligation / Legitimate interest</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              If we rely on consent, you may withdraw it at any time via account
              settings or by emailing us.
            </p>
            <hr className="border-white/20 my-4" />
            <h2 className="text-xl text-white/90 font-medium mt-6">
              6. Retention
            </h2>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                <span className="font-semibold">AI chat logs</span> — deleted{" "}
                <span className="font-semibold">30 days</span> after creation.
              </li>
              <li>
                <span className="font-semibold">
                  Account &amp; itinerary data
                </span>
                &nbsp;— retained until you delete your account or{" "}
                <span className="font-semibold">36 months</span> after your last
                login, whichever is sooner.
              </li>
              <li>
                <span className="font-semibold">Transactional records</span>
                &nbsp;— retained <span className="font-semibold">
                  7 years
                </span>{" "}
                to satisfy tax and bookkeeping laws.
              </li>
              <li>
                <span className="font-semibold">Back-ups &amp; logs</span>
                &nbsp;— cycled every{" "}
                <span className="font-semibold">90 days</span>.
              </li>
            </ul>
            <p>
              Aggregated or de-identified data that cannot identify you may be
              kept indefinitely.
            </p>
            <hr className="border-white/20 my-4" />
            <h2 className="text-xl text-white/90 font-medium mt-6">
              7. How We Share Information
            </h2>
            <ol className="list-decimal pl-6 space-y-2 mt-2">
              <li>
                <span className="font-semibold">Service providers</span> under
                written contract.
              </li>
              <li>
                <span className="font-semibold">Travel partners</span> receive
                limited trip parameters to fulfil your search or booking; they
                operate under their own privacy notices.
              </li>
              <li>
                <span className="font-semibold">Professional advisers</span>{" "}
                (lawyers, accountants, insurers) bound by confidentiality.
              </li>
              <li>
                <span className="font-semibold">Authorities</span> when legally
                required or to protect rights, safety or property.
              </li>
              <li>
                <span className="font-semibold">Business transfers</span> — if
                we merge, sell or reorganise, your data will transfer subject to
                this Policy.
              </li>
            </ol>
            <p>
              We <span className="font-semibold">never</span> sell Personal
              Information or share it for cross-context behavioural advertising.
            </p>
            <hr className="border-white/20 my-4" />
            <h2 className="text-xl text-white/90 font-medium mt-6">
              8. International Data Transfers
            </h2>
            <p>
              We operate globally. Your data may be processed in{" "}
              <span className="font-semibold">
                Australia, the United Kingdom, the European Union or the United
                States
              </span>
              . We rely on:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                <span className="font-semibold">
                  Standard Contractual Clauses (SCCs)
                </span>{" "}
                approved by the European Commission;
              </li>
              <li>
                <span className="font-semibold">UK Addendum</span> to the SCCs
                for UK transfers;
              </li>
              <li>
                <span className="font-semibold">
                  Cloudflare Binding Corporate Rules
                </span>
                ;
              </li>
              <li>Strong encryption in transit and at rest.</li>
            </ul>
            <p>
              A copy of relevant transfer safeguards is available upon request.
            </p>
            <hr className="border-white/20 my-4" />
            <h2 className="text-xl text-white/90 font-medium mt-6">
              9. Cookies &amp; Similar Technologies
            </h2>
            <p>We classify cookies as:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                <span className="font-semibold">Strictly necessary</span> —
                authentication, load-balancing, fraud prevention.
              </li>
              <li>
                <span className="font-semibold">
                  Analytics &amp; performance
                </span>
                &nbsp;— to understand feature adoption and improve UX.
              </li>
            </ul>
            <p>
              When you first visit, a banner allows you to accept or reject
              non-essential cookies. Browser controls and third-party plug-ins
              (e.g. uBlock Origin) offer additional options.
            </p>
            <p>
              See our{" "}
              <span className="underline cursor-pointer text-indigo-400 hover:text-indigo-300">
                [Cookie Notice]
              </span>{" "}
              for details.
            </p>
            <hr className="border-white/20 my-4" />
            <h2 className="text-xl text-white/90 font-medium mt-6">
              10. Your Rights &amp; Choices
            </h2>
            <p>Depending on your location, you may have rights to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                <span className="font-semibold">Access</span> a copy of your
                Personal Information;
              </li>
              <li>
                <span className="font-semibold">Correct</span> inaccurate or
                incomplete data;
              </li>
              <li>
                <span className="font-semibold">Delete/erase</span> data in
                certain circumstances;
              </li>
              <li>
                <span className="font-semibold">Restrict</span> or{" "}
                <span className="font-semibold">object</span> to processing;
              </li>
              <li>
                <span className="font-semibold">Port</span> data you provided in
                a structured, machine-readable format;
              </li>
              <li>
                <span className="font-semibold">Opt-out</span> of marketing at
                any time;
              </li>
              <li>
                Lodge a <span className="font-semibold">complaint</span> with a
                supervisory authority (EU/UK) or the OAIC (Australia);
              </li>
              <li>
                <span className="font-semibold">
                  Shine the Light / Do Not Sell or Share
                </span>{" "}
                (California). We do not sell or share as defined by CPRA.
              </li>
            </ul>
            <p>
              Submit requests via{" "}
              <span className="font-semibold">support@waytora.com</span> or the
              in-app privacy centre. We respond within{" "}
              <span className="font-semibold">30 days</span> (or 45 days for
              California). Verification of identity may be required.
            </p>
            <hr className="border-white/20 my-4" />
            <h2 className="text-xl text-white/90 font-medium mt-6">
              11. Security Measures
            </h2>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>HTTPS/TLS 1.2+ for all traffic.</li>
              <li>
                AES-256 encryption at rest for databases and object storage.
              </li>
              <li>
                Role-based access controls, MFA for admin accounts,
                least-privilege keys.
              </li>
              <li>
                Continuous vulnerability scanning, automated patch management,
                annual penetration testing.
              </li>
              <li>Incident-response plan aligned with ISO 27001/2.</li>
            </ul>
            <p>
              Despite protective measures, no system is 100 % secure; please use
              caution when sharing information online.
            </p>
            <hr className="border-white/20 my-4" />
            <h2 className="text-xl text-white/90 font-medium mt-6">
              12. Children
            </h2>
            <p>
              The Services are{" "}
              <span className="font-semibold">
                not directed to children under 13
              </span>
              . We do not knowingly collect Personal Information from children.
              If you believe a child has provided data, contact us and we will
              delete it.
            </p>
            <hr className="border-white/20 my-4" />
            <h2 className="text-xl text-white/90 font-medium mt-6">
              13. Job Applicants
            </h2>
            <p>
              Information submitted through our careers page (CVs, cover
              letters, references, background-check results) is used solely for
              recruitment, onboarding and compliance, and may be retained for up
              to <span className="font-semibold">24 months</span> or as required
              by law.
            </p>
            <hr className="border-white/20 my-4" />
            <h2 className="text-xl text-white/90 font-medium mt-6">
              14. Links to Third-Party Sites
            </h2>
            <p>
              The Services may contain links or integrations to third-party
              sites (e.g. airlines, hotels, social-media platforms). We are not
              responsible for their privacy practices. Review their policies
              before providing information.
            </p>
            <hr className="border-white/20 my-4" />
            <h2 className="text-xl text-white/90 font-medium mt-6">
              15. Changes to This Policy
            </h2>
            <p>
              We may update this Policy from time to time. Material changes will
              be announced via email or an in-app banner{" "}
              <span className="font-semibold">at least 14 days</span> before the
              new version becomes effective. The "Last updated" date at the top
              will reflect the revision.
            </p>
            <hr className="border-white/20 my-4" />
            <h2 className="text-xl text-white/90 font-medium mt-6">
              16. Contact Us
            </h2>
            <blockquote className="border-l-4 border-indigo-400 pl-4 text-gray-200">
              Waytora Pty Ltd
              <br />
              Attn: Privacy Team
              <br />
              Level 1, 457-459 Elizabeth Street
              <br />
              Surry Hills NSW 2010, Australia
              <br />
              <span className="font-semibold">Email:</span> support@waytora.com
            </blockquote>
            <hr className="border-white/20 my-4" />
            <h2 className="text-xl text-white/90 font-medium mt-6">
              17. Definitions
            </h2>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>
                <span className="font-semibold">Services</span> — All websites,
                apps, content and products controlled by Waytora that display
                this Policy.
              </li>
              <li>
                <span className="font-semibold">GDPR</span> — Regulation (EU)
                2016/679 and UK GDPR.
              </li>
              <li>
                <span className="font-semibold">CCPA/CPRA</span> — California
                Consumer Privacy Act of 2018 as amended by the California
                Privacy Rights Act of 2020.
              </li>
              <li>
                <span className="font-semibold">Australian Privacy Act</span> —
                Privacy Act 1988 (Cth) including the Australian Privacy
                Principles (APPs).
              </li>
            </ul>
            <p className="text-xs text-gray-400 mt-6">
              © 2025 Waytora Pty Ltd. All rights reserved.
            </p>
          </div>
          <div className="flex justify-center mt-8">
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-2.5 text-sm text-white border border-white/30 rounded-lg hover:bg-white/10 transition-all duration-300 backdrop-blur-sm"
            >
              Back
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />

      <BackToTopButton />
    </div>
  );
};

export default PrivacyPolicyPage;
